import React from 'react';
import ReportingPageLayout from './ReportingPageLayout';
import useReportingPage from '../hooks/useReportingPage';

// Only reports that actually have a built page + route appear here.
// Add an entry as each Donations sub-report ships.
const REPORTS = [
  {
    key: 'donations_all_donors',
    title: 'All Donors',
    description: 'Every donor with giving totals, filterable by amount, recency, gift month and campaign.',
    path: (teamId) => `/teams/${teamId}/reporting/donations/all-donors`,
  },
  {
    key: 'donations_top_donors',
    title: 'Top Donors',
    description: 'Your donors ranked by total giving, with gift counts and last gift date.',
    path: (teamId) => `/teams/${teamId}/reporting/donations/top-donors`,
  },
  {
    key: 'donations_outstanding_pledges',
    title: 'Outstanding Pledges',
    description: 'Open pledges with aging, overdue balance, and next payment due.',
    path: (teamId) => `/teams/${teamId}/reporting/donations/outstanding-pledges`,
  },
  {
    key: 'donations_giving_by_campaign',
    title: 'Giving by Campaign',
    description: 'Total raised per campaign with donor counts and goal progress.',
    path: (teamId) => `/teams/${teamId}/reporting/donations/giving-by-campaign`,
  },
  {
    key: 'donations_giving_by_fund',
    title: 'Giving by Fund',
    description: 'Total raised per fund with donor and campaign counts.',
    path: (teamId) => `/teams/${teamId}/reporting/donations/giving-by-fund`,
  },
  {
    key: 'donations_year_over_year',
    title: 'Year-over-Year Comparison',
    description: 'Total raised, donors, average gift, and new donors compared across fiscal years.',
    path: (teamId) => `/teams/${teamId}/reporting/donations/year-over-year`,
  },
  {
    key: 'donations_lybunt_sybunt',
    title: 'LYBUNT/SYBUNT',
    description: 'Lapsed donors who gave before but not this year, with last gift and a suggested ask.',
    path: (teamId) => `/teams/${teamId}/reporting/donations/lybunt-sybunt`,
  },
  {
    key: 'donations_year_end_summary',
    title: 'Year-End Summaries',
    description: 'Generate a mailable donation summary PDF for every donor in a date range.',
    path: (teamId) => `/teams/${teamId}/reporting/donations/year-end-summary`,
  },
  {
    key: 'donations_thank_you',
    title: 'General Thank You',
    description: 'Gift-level mailing, salutation and contact data with filters and CSV export.',
    path: (teamId) => `/teams/${teamId}/reporting/donations/thank-you`,
  },
];

function ReportingDonationsPage({ team }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage('year_to_date');

  const teamId = team?.id;

  return (
    <ReportingPageLayout
      team={team}
      activeKey="donations"
      title="Donations"
      subtitle="Donor and giving reports for your organization."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={[]}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
      showDatePicker={false}
      showFilters={false}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}
      >
        {REPORTS.map((report) => (
          <a
            key={report.key}
            href={teamId ? report.path(teamId) : '#'}
            className="text-decoration-none d-block"
            style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: 20, color: 'inherit' }}
          >
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--sl-text-primary, #101828)', marginBottom: 6 }}>
              {report.title}
            </div>
            <div style={{ fontSize: 13, color: 'var(--sl-text-quaternary, #667085)', lineHeight: 1.5 }}>
              {report.description}
            </div>
          </a>
        ))}
      </div>
    </ReportingPageLayout>
  );
}

export default ReportingDonationsPage;
